import { useCallback, useMemo, useState } from 'react';
import type { ThemeNames } from '@tenants/types';
import type { TopMarket } from '../../local-focus/top-markets';
import type { ListingCardData } from 'components/listing-card/listing-card';
import {
  fetchForSaleAreaListings,
  fetchSoldListingsForArea,
} from './fetch-area-listings';
import { getCoordinatesForSlug } from './helpers';

export type ZoocasaLightHomeSectionsState = {
  forSaleListings: ListingCardData[];
  forSaleCount: number;
  forSaleIsExpandedArea: boolean;
  forSaleLocationLabel: string;
  soldListings: ListingCardData[];
  featuredListings: ListingCardData[];
  homesForSaleUrl: string;
  mapViewUrl: string;
};

function homeSectionsFromSsr(params: {
  availableHomeListings: ListingCardData[];
  availableHomeListingsMeta: number;
  availableHomeListingsLocation: string;
  isExpandedArea: boolean;
  soldHomeListings: ListingCardData[];
  featuredHomeListings: ListingCardData[];
  initialHomesForSaleUrl: string;
  initialMapViewUrl: string;
}): ZoocasaLightHomeSectionsState {
  return {
    forSaleListings: params.availableHomeListings,
    forSaleCount: params.availableHomeListingsMeta,
    forSaleIsExpandedArea: params.isExpandedArea,
    forSaleLocationLabel: params.availableHomeListingsLocation,
    soldListings: params.soldHomeListings,
    featuredListings: params.featuredHomeListings,
    homesForSaleUrl: params.initialHomesForSaleUrl,
    mapViewUrl: params.initialMapViewUrl,
  };
}

export type locationParams = {
  slug: string;
  areaName: string;
  latitude: number;
  longitude: number;
  subLocation: string;
};

export function useZoocasaLightHomepageSections(options: {
  tenantThemeId: ThemeNames;
  isCanadianSite: boolean;

  initialHomesForSaleUrl: string;

  initialMapViewUrl: string;
  initialSoldPricesUrl: string;
  availableHomeListings: ListingCardData[];
  availableHomeListingsMeta: number;
  availableHomeListingsLocation: string;
  isExpandedArea: boolean;

  currentSlug: string;
  currentAreaName: string;
  currentLatitude: number;
  currentLongitude: number;
  soldHomeListings: ListingCardData[];
  featuredHomeListings: ListingCardData[];
  setHomePageLocationParams: (payload: locationParams) => void;
}) {
  const {
    tenantThemeId,
    isCanadianSite,
    initialHomesForSaleUrl,
    initialMapViewUrl,
    initialSoldPricesUrl,
    availableHomeListings,
    availableHomeListingsMeta,
    availableHomeListingsLocation,
    isExpandedArea,
    currentSlug,
    soldHomeListings,
    featuredHomeListings,
    setHomePageLocationParams: onMarketAppliedToLocation,
  } = options;

  const [homeSections, setHomeSections] = useState<ZoocasaLightHomeSectionsState>(() =>
    homeSectionsFromSsr({
      availableHomeListings,
      availableHomeListingsMeta,
      availableHomeListingsLocation,
      isExpandedArea,
      soldHomeListings,
      featuredHomeListings,
      initialHomesForSaleUrl,
      initialMapViewUrl,
    })
  );

  const resetHomeSectionsFromProps = useCallback(() => {
    setHomeSections(
      homeSectionsFromSsr({
        availableHomeListings,
        availableHomeListingsMeta,
        availableHomeListingsLocation,
        isExpandedArea,
        soldHomeListings,
        featuredHomeListings,
        initialHomesForSaleUrl,
        initialMapViewUrl,
      })
    );
  }, [
    availableHomeListings,
    availableHomeListingsMeta,
    availableHomeListingsLocation,
    isExpandedArea,
    soldHomeListings,
    featuredHomeListings,
    initialHomesForSaleUrl,
    initialMapViewUrl,
  ]);

  const applyPopularCityChange = useCallback(
    async (market: TopMarket) => {
      try {
        const areaListings = await fetchForSaleAreaListings({
          tenantThemeId,
          isCanadianSite,
          slug: market.slug,
          areaLabel: market.name,
        });
        const { latitude, longitude } = await getCoordinatesForSlug(
          areaListings.slug,
          isCanadianSite
        );

        onMarketAppliedToLocation({
          slug: areaListings.slug,
          areaName: areaListings.label,
          latitude,
          longitude,
          subLocation: '',
        });

        const resolvedSlug = areaListings.slug;
        const resolvedLabel = areaListings.label;
        const homesForSaleUrl = `/${resolvedSlug}-real-estate`;
        const mapViewUrl = `/search?latitude=${latitude}&longitude=${longitude}&zoom=12&area-name=${encodeURIComponent(resolvedLabel)}`;

        const soldAreaListings = await fetchSoldListingsForArea({
          tenantThemeId,
          isCanadianSite,
          slug: areaListings.slug,
          areaLabel: areaListings.label,
        }).catch(e => {
          console.error('Zoocasa light: failed to load sold listings for selected city', e);
          return null;
        });

        setHomeSections({
          forSaleListings: areaListings.cardRows,
          forSaleCount: areaListings.totalCount,
          forSaleIsExpandedArea: areaListings.isExpandedArea,
          forSaleLocationLabel: areaListings.label,
          soldListings: soldAreaListings?.cardRows ?? [],
          featuredListings: featuredHomeListings,
          homesForSaleUrl,
          mapViewUrl,
        });
      } catch (e) {
        console.error('Zoocasa light: failed to load listings for selected city', e);
      }
    },
    [tenantThemeId, isCanadianSite, onMarketAppliedToLocation, featuredHomeListings]
  );

  const onPopularCityChange = useCallback(
    (market: TopMarket) => {
      applyPopularCityChange(market);
    },
    [applyPopularCityChange]
  );

  const slugForMarketingPaths = useMemo(() => currentSlug.trim(), [currentSlug]);

  const soldPricesUrl = useMemo(() => {
    const t = slugForMarketingPaths;
    return t ? `/${t}-real-estate/sold` : initialSoldPricesUrl;
  }, [slugForMarketingPaths, initialSoldPricesUrl]);

  return {
    resetHomeSectionsFromProps,
    applyPopularCityChange,
    onPopularCityChange,
    homesForSaleUrl: homeSections.homesForSaleUrl,
    mapViewUrl: homeSections.mapViewUrl,
    soldPricesUrl,
    forSaleLocationLabel: homeSections.forSaleLocationLabel,
    displayedForSaleListings: homeSections.forSaleListings,
    displayedForSaleListingCount: homeSections.forSaleCount,
    displayedForSaleIsExpandedArea: homeSections.forSaleIsExpandedArea,
    soldListingsForSection: homeSections.soldListings,
    featuredListingsForSection: homeSections.featuredListings,
  };
}
